// Submit requirements (docs/contract.md §4). Errors block submit; warnings never do.
// Required: step 1, every step 2 tool row, the step 3 active client count. The rest can wait for the call.
import { FUNCTIONS } from './catalog.js';
import { isUnanswered } from './answers.js';
import { breakdownSum } from './rules.js';
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const blank = (v) => v === undefined || v === null || (typeof v === 'string' && v.trim() === '');
function step1(a) {
    const out = [];
    const req = (path, v, label) => {
        if (blank(v))
            out.push({ path, severity: 'error', code: 'required', message: `${label} is required.`, step: 1 });
    };
    req('firmName', a.firmName, 'Firm name');
    req('contact.name', a.contact?.name, 'Your name');
    req('contact.email', a.contact?.email, 'Email');
    if (!blank(a.contact?.email) && !EMAIL.test(a.contact.email.trim())) {
        out.push({ path: 'contact.email', severity: 'error', code: 'invalid_email', message: 'Enter a valid email address.', step: 1 });
    }
    req('contact.phone', a.contact?.phone, 'Phone');
    req('statesText', a.statesText, 'States you practice in');
    req('logins', a.logins, 'Logins needed');
    req('firmSize', a.firmSize, 'People at the firm');
    if (!a.services?.length)
        out.push({ path: 'services', severity: 'error', code: 'required', message: 'Pick at least one service.', step: 1 });
    // A specific date answers "when" on its own; otherwise pick a general timeframe.
    if (blank(a.targetTiming) && blank(a.targetDate)) {
        out.push({ path: 'targetTiming', severity: 'error', code: 'required', message: 'Pick a timeframe or enter a specific date.', step: 1 });
    }
    return out;
}
function step2(rows) {
    const out = [];
    for (const f of FUNCTIONS) {
        const row = rows[f.id];
        if (row && !row.notApplicable && row.other !== null && row.other.trim() === '') {
            out.push({ path: `tools.${f.id}.other`, severity: 'error', code: 'other_required', message: `Type the other tool you use for "${f.label}".`, step: 2 });
        }
    }
    // Every tool row needs an answer at submit: a tool, Other (typed), or Not applicable.
    // The two open questions after the rows (integrationsText, fallbackText) stay optional.
    for (const f of FUNCTIONS.filter((x) => isUnanswered(rows[x.id]))) {
        out.push({ path: `tools.${f.id}`, severity: 'error', code: 'required', message: `"${f.label}" needs an answer.`, step: 2 });
    }
    return out;
}
function step3(a) {
    const out = [];
    if (blank(a.clientsTotal))
        out.push({ path: 'clientsTotal', severity: 'error', code: 'required', message: 'Active clients is required.', step: 3 });
    const sum = breakdownSum(a);
    if (sum !== null && typeof a.clientsTotal === 'number' && sum !== a.clientsTotal) {
        out.push({ path: 'breakdown', severity: 'warning', code: 'breakdown_mismatch', step: 3, message: `These add up to ${sum}, and you said about ${a.clientsTotal}. Rough numbers are fine.` });
    }
    for (const [k, label] of [['openJobs', 'Open returns or jobs'], ['groups', 'Related clients'], ['templates', 'Checklists or templates'], ['clientList', 'Client list'], ['timeBilling', 'Billing from time'], ['renewalMonth', 'Engagement letter renewal']]) {
        if (blank(a[k]))
            out.push({ path: k, severity: 'warning', code: 'unanswered', message: `${label}: no answer yet.`, step: 3 });
    }
    return out;
}
function step4(a) {
    const out = [];
    if (!a.docsScope?.length)
        out.push({ path: 'docsScope', severity: 'warning', code: 'unanswered', message: 'Final documents: no answer yet.', step: 4 });
    if (a.docsScope?.length && !a.docsScope.includes('none')) {
        if (!a.docsYears)
            out.push({ path: 'docsYears', severity: 'warning', code: 'unanswered', message: 'How far back: no answer yet.', step: 4 });
        if (!a.docsSize)
            out.push({ path: 'docsSize', severity: 'warning', code: 'unanswered', message: 'Approximate size: no answer yet.', step: 4 });
    }
    return out;
}
export function validateStep(step, a, rows) {
    switch (step) {
        case 1: return step1(a);
        case 2: return step2(rows);
        case 3: return step3(a);
        case 4: return step4(a);
        default: return [];
    }
}
export function validateForSubmit(a, rows) {
    return [...step1(a), ...step2(rows), ...step3(a), ...step4(a)];
}
export const hasErrors = (issues) => issues.some((i) => i.severity === 'error');
//# sourceMappingURL=validate.js.map